import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import TiptapEditor from '../../components/Editor/TiptapEditor';
import MultiSelectBusca from '../../components/MultiSelectBusca';
import { paraSalvar, paraExibicao } from '../../components/Editor/tiptapBase';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';
import { formatarData, paraInputData } from '../../utils/datas';

const parse = (c) => { if (!c) return null; if (typeof c === 'object') return c; try { return JSON.parse(c); } catch (_) { return null; } };
const tarefaVazia = () => ({ descricao: '', responsavel_id: '', prazo: '' });

export default function AtaEditor() {
  const { id } = useParams();
  const editando = !!id;
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { token } = useAuth();

  const [pastas, setPastas] = useState([]);
  const [pessoas, setPessoas] = useState([]);
  const [pronto, setPronto] = useState(!editando);
  const [salvando, setSalvando] = useState(false);

  const [f, setF] = useState({
    pasta_id: sp.get('pasta') || '',
    titulo: '',
    data_reuniao: new Date().toISOString().slice(0, 10),
    proxima_reuniao: '',
  });
  const [conteudo, setConteudo] = useState(null);
  const [tarefas, setTarefas] = useState([]);      // já salvas (ou locais, se ata nova)
  const [nova, setNova] = useState(tarefaVazia());
  const [editandoTarefa, setEditandoTarefa] = useState(null);

  const set = (campo, valor) => setF(s => ({ ...s, [campo]: valor }));

  useEffect(() => {
    api.get('/atas/pastas').then(({ data }) => setPastas(data.pastas)).catch(() => {});
    api.get('/atas/pessoas').then(({ data }) => setPessoas(data.pessoas)).catch(() => {});
  }, []);

  function carregarTarefas() {
    if (!editando) return;
    api.get(`/atas/${id}`).then(({ data }) => setTarefas(data.tarefas)).catch(() => {});
  }

  useEffect(() => {
    if (!editando) return;
    api.get(`/atas/${id}`).then(({ data }) => {
      const a = data.ata;
      setF({
        pasta_id: a.pasta_id || '', titulo: a.titulo || '',
        data_reuniao: paraInputData(a.data_reuniao), proxima_reuniao: paraInputData(a.proxima_reuniao),
      });
      setConteudo(paraExibicao(parse(a.conteudo), token));
      setTarefas(data.tarefas);
      setPronto(true);
    }).catch(() => {
      Swal.fire('Erro', 'Não foi possível carregar a ata.', 'error');
      navigate('/atas');
    });
  }, [id, editando, navigate, token]);

  async function adicionarTarefa() {
    if (!nova.descricao.trim()) return Swal.fire('Falta a tarefa', 'Escreva o que ficou combinado.', 'warning');
    const t = {
      descricao: nova.descricao.trim(),
      responsavel_id: nova.responsavel_id || null,
      prazo: nova.prazo || null,
    };
    if (!editando) {
      // Ata nova: guarda aqui e vai junto no primeiro salvamento
      setTarefas(prev => [...prev, { ...t, id: `novo-${Date.now()}`, status: 'aberto', responsavel_nome: pessoas.find(p => String(p.id) === String(t.responsavel_id))?.nome || null }]);
      setNova(tarefaVazia());
      return;
    }
    try {
      await api.post(`/atas/${id}/tarefas`, t);
      setNova(tarefaVazia());
      carregarTarefas();
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível criar a tarefa.', 'error');
    }
  }

  async function removerTarefa(t) {
    if (!editando || String(t.id).startsWith('novo-')) { setTarefas(prev => prev.filter(x => x.id !== t.id)); return; }
    const r = await Swal.fire({
      icon: 'warning', title: 'Remover esta tarefa?', text: t.descricao,
      showCancelButton: true, confirmButtonText: 'Sim, remover', cancelButtonText: 'Cancelar', confirmButtonColor: '#C62828',
    });
    if (!r.isConfirmed) return;
    try { await api.delete(`/atas/pendencias/${t.id}`); carregarTarefas(); }
    catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Não foi possível remover.', 'error'); }
  }

  async function salvarTarefaEditada() {
    const t = editandoTarefa;
    if (!t.descricao.trim()) return Swal.fire('Falta a tarefa', 'Escreva o que ficou combinado.', 'warning');
    const payload = {
      descricao: t.descricao.trim(), responsavel_id: t.responsavel_id || null, prazo: t.prazo || null,
    };
    if (String(t.id).startsWith('novo-')) {
      setTarefas(prev => prev.map(x => (x.id === t.id
        ? { ...x, ...payload, responsavel_nome: pessoas.find(p => String(p.id) === String(payload.responsavel_id))?.nome || null }
        : x)));
      setEditandoTarefa(null);
      return;
    }
    try { await api.put(`/atas/pendencias/${t.id}`, payload); setEditandoTarefa(null); carregarTarefas(); }
    catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Não foi possível salvar a tarefa.', 'error'); }
  }

  async function salvar(e) {
    e.preventDefault();
    if (!f.pasta_id) return Swal.fire('Falta a pasta', 'Escolha em qual reunião esta ata entra.', 'warning');
    if (!f.titulo.trim()) return Swal.fire('Falta o título', 'Dê um título à ata.', 'warning');
    setSalvando(true);
    const payload = {
      pasta_id: f.pasta_id, titulo: f.titulo.trim(),
      data_reuniao: f.data_reuniao || null, proxima_reuniao: f.proxima_reuniao || null,
      conteudo: conteudo ? paraSalvar(conteudo) : null,
    };
    try {
      if (editando) {
        await api.put(`/atas/${id}`, payload);
        await Swal.fire({ icon: 'success', title: 'Ata salva!', timer: 1200, showConfirmButton: false });
        navigate(`/atas/${id}`);
      } else {
        const { data } = await api.post('/atas', {
          ...payload,
          tarefas: tarefas.map(t => ({ descricao: t.descricao, responsavel_id: t.responsavel_id, prazo: t.prazo })),
        });
        await Swal.fire({ icon: 'success', title: 'Ata criada!', timer: 1200, showConfirmButton: false });
        navigate(`/atas/${data.id}/editar`);
      }
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível salvar.', 'error');
    } finally { setSalvando(false); }
  }

  async function excluirAta() {
    const r = await Swal.fire({
      icon: 'warning', title: 'Excluir esta ata?', text: 'Ela sai da pasta junto com as tarefas dela.',
      showCancelButton: true, confirmButtonText: 'Sim, excluir', cancelButtonText: 'Cancelar', confirmButtonColor: '#C62828',
    });
    if (!r.isConfirmed) return;
    try { await api.delete(`/atas/${id}`); navigate('/atas'); }
    catch (err) { Swal.fire('Erro', err.response?.data?.error || 'Não foi possível excluir.', 'error'); }
  }

  const nomePessoa = (idp) => pessoas.find(p => String(p.id) === String(idp))?.nome || null;
  const pessoasOptions = pessoas.map(p => ({ value: p.id, label: p.nome, hint: p.cargo || '' }));

  return (
    <>
      <Header />
      <main className="conteudo-container" style={{ maxWidth: 900 }}>
        <Link to="/atas" className="doc-voltar"><i className="bi bi-arrow-left"></i> Atas</Link>
        <h1 style={{ color: 'var(--verde)', fontWeight: 700, marginBottom: 16 }}>{editando ? 'Editar ata' : 'Nova ata'}</h1>

        {!pronto ? <div className="conteudo-card">Carregando…</div> : (
          <>
            <form className="conteudo-card doc-form" onSubmit={salvar}>
              <div className="doc-form-linha">
                <div style={{ flex: 1 }}>
                  <label className="doc-label">Reunião (pasta) *</label>
                  <select className="doc-input" value={f.pasta_id} onChange={e => set('pasta_id', e.target.value)}>
                    <option value="">Escolha…</option>
                    {pastas.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="doc-label">Data da reunião</label>
                  <input type="date" className="doc-input" value={f.data_reuniao} onChange={e => set('data_reuniao', e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="doc-label">Próxima reunião</label>
                  <input type="date" className="doc-input" value={f.proxima_reuniao} onChange={e => set('proxima_reuniao', e.target.value)} />
                </div>
              </div>

              <label className="doc-label">Título *</label>
              <input className="doc-input" value={f.titulo} maxLength={200} autoFocus
                     onChange={e => set('titulo', e.target.value)} placeholder="Ex.: Reunião de Gerentes de Venda — 17/09/2026" />

              <label className="doc-label">Pauta e decisões</label>
              <TiptapEditor valorInicial={conteudo} onChange={setConteudo} />

              <div className="doc-form-acoes">
                <button type="submit" className="btn-centrao" disabled={salvando}>
                  {salvando ? 'Salvando…' : (editando ? 'Salvar ata' : 'Criar ata')}
                </button>
                <Link to="/atas" className="btn btn-outline-secondary">Cancelar</Link>
                {editando && (
                  <button type="button" className="btn btn-outline-danger" style={{ marginLeft: 'auto' }} onClick={excluirAta}>
                    <i className="bi bi-trash me-1"></i> Excluir ata
                  </button>
                )}
              </div>
            </form>

            <h2 className="trilha-secao">Tarefas da reunião</h2>
            <p className="adm-dica" style={{ marginTop: -6, marginBottom: 10 }}>
              {editando
                ? 'Cada tarefa é salva na hora — dá pra ir criando enquanto a reunião acontece. O responsável vê em "Minhas tarefas" e marca como feita.'
                : 'Adicione as tarefas agora; elas são gravadas junto quando você criar a ata.'}
            </p>

            <div className="conteudo-card" style={{ marginBottom: 12 }}>
              <div className="ata-nova-tarefa">
                <input
                  className="doc-input ata-tarefa-desc"
                  value={nova.descricao}
                  placeholder="O que ficou combinado? Ex.: transferir portas de CP pro CD"
                  onChange={e => setNova(s => ({ ...s, descricao: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); adicionarTarefa(); } }}
                />
                <div className="ata-tarefa-resp">
                  {/* key muda a cada tarefa adicionada: remonta o campo e limpa o texto
                      digitado na busca (o componente guarda esse texto por dentro). */}
                  <MultiSelectBusca
                    key={`resp-nova-${tarefas.length}`}
                    options={pessoasOptions}
                    values={nova.responsavel_id ? [nova.responsavel_id] : []}
                    onChange={vals => setNova(s => ({ ...s, responsavel_id: vals.length ? vals[vals.length - 1] : '' }))}
                    placeholder="Buscar responsável…"
                    icon="bi-person"
                  />
                </div>
                <input type="date" className="doc-input ata-tarefa-prazo" value={nova.prazo}
                       onChange={e => setNova(s => ({ ...s, prazo: e.target.value }))} />
                <button type="button" className="btn-centrao" onClick={adicionarTarefa}>
                  <i className="bi bi-plus-lg me-1"></i> Adicionar
                </button>
              </div>
            </div>

            <div className="adm-tabela-wrap">
              <table className="adm-tabela">
                <thead><tr><th>O que ficou combinado</th><th>Responsável</th><th>Prazo</th><th>Situação</th><th></th></tr></thead>
                <tbody>
                  {tarefas.map(t => (
                    editandoTarefa?.id === t.id ? (
                      <tr key={t.id}>
                        <td><input className="doc-input" value={editandoTarefa.descricao} onChange={e => setEditandoTarefa(s => ({ ...s, descricao: e.target.value }))} /></td>
                        <td style={{ minWidth: 200 }}>
                          <MultiSelectBusca
                            options={pessoasOptions}
                            values={editandoTarefa.responsavel_id ? [editandoTarefa.responsavel_id] : []}
                            onChange={vals => setEditandoTarefa(s => ({ ...s, responsavel_id: vals.length ? vals[vals.length - 1] : '' }))}
                            placeholder="Buscar responsável…"
                            icon="bi-person"
                          />
                        </td>
                        <td><input type="date" className="doc-input" value={paraInputData(editandoTarefa.prazo)} onChange={e => setEditandoTarefa(s => ({ ...s, prazo: e.target.value }))} /></td>
                        <td colSpan={2}>
                          <div className="adm-acoes">
                            <button type="button" className="btn-centrao" onClick={salvarTarefaEditada}>Salvar</button>
                            <button type="button" className="btn btn-outline-secondary" onClick={() => setEditandoTarefa(null)}>Cancelar</button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={t.id}>
                        <td>{t.descricao}</td>
                        <td>{t.responsavel_nome || nomePessoa(t.responsavel_id) || <span className="adm-sub">sem responsável</span>}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatarData(t.prazo) || <span className="adm-sub">—</span>}</td>
                        <td>
                          <span className={`adm-badge adm-badge--${t.status === 'concluido' ? 'ok' : 'alerta'}`}>
                            {t.status === 'concluido' ? 'Concluída' : t.status === 'em_andamento' ? 'Em andamento' : 'Aberta'}
                          </span>
                        </td>
                        <td>
                          <div className="adm-acoes">
                            <button type="button" className="adm-btn-icone" title="Editar"
                                    onClick={() => setEditandoTarefa({ ...t, prazo: paraInputData(t.prazo) })}>
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button type="button" className="adm-btn-icone adm-btn-icone--perigo" title="Remover" onClick={() => removerTarefa(t)}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                  {tarefas.length === 0 && <tr><td colSpan={5} className="adm-vazio">Nenhuma tarefa ainda. Adicione acima conforme a reunião for andando.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </>
  );
}
